import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useInfiniteList, useAutoLoad } from "@/hooks/useInfiniteList";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { label, money } from "@/lib/constants";
import { StatusBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, Receipt, Send, CreditCard, Trash2, Wand2, Ban, Bell } from "lucide-react";

const FILTERS = ["all", "draft", "sent", "viewed", "partially_paid", "paid", "overdue"];

export default function Invoices() {
  const { user } = useAuth();
  const canCreate = can(user, "invoice:create");
  const canUpdate = can(user, "invoice:update");
  const canPay = can(user, "invoice:pay");
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [filter, setFilter] = useState("all");
  const filterParams = useMemo(() => (filter === "all" ? {} : { status: filter }), [filter]);
  const { items: rows, loadMore: loadMoreRows, hasMore: hasMoreRows,
          reload: reloadRows } = useInfiniteList("/invoices",
                                                 { params: filterParams, pageSize: 100 });
  const rowsSentinel = useAutoLoad(loadMoreRows, hasMoreRows);
  const [open, setOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gen, setGen] = useState({ cadence: "weekly", period_start: "", period_end: "" });
  const [form, setForm] = useState({
    owner_id: "", guest_name: "", guest_email: "", property_id: "", cadence: "manual",
    tax_rate: 0, discount: 0, notes: "",
    line_items: [{ description: "", quantity: 1, unit_price: 0 }],
  });

  const load = useCallback(() => {
    if (canCreate) {
      api.get("/users", { params: { role: "owner" } }).then(({ data }) => setOwners(data));
      api.get("/properties").then(({ data }) => setProperties(data));
    }
    return reloadRows();
  }, [canCreate, reloadRows]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = form.line_items.reduce((sum, i) => sum + Number(i.quantity || 0) * Number(i.unit_price || 0), 0);

  const setItem = (idx, patch) =>
    setForm((f) => ({ ...f, line_items: f.line_items.map((i, x) => (x === idx ? { ...i, ...patch } : i)) }));

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/invoices", {
        ...form,
        owner_id: form.owner_id || null,
        guest_name: form.owner_id ? null : form.guest_name.trim(),
        guest_email: form.owner_id ? null : form.guest_email.trim(),
        send_now: true,
        property_id: form.property_id || null,
        tax_rate: Number(form.tax_rate || 0),
        discount: Number(form.discount || 0),
        line_items: form.line_items
          .filter((i) => i.description)
          .map((i) => ({ ...i, quantity: Number(i.quantity || 0), unit_price: Number(i.unit_price || 0) })),
      });
      toast.success(data.emailed
        ? `Invoice ${data.number} emailed to ${data.owner_name} with a Pay Now link`
        : `Invoice ${data.number} issued, but the email could not be delivered right now — open it and tap "Send payment reminder" to retry`);
      setOpen(false);
      setForm({ owner_id: "", guest_name: "", guest_email: "", property_id: "", cadence: "manual",
        tax_rate: 0, discount: 0, notes: "",
        line_items: [{ description: "", quantity: 1, unit_price: 0 }] });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create invoice");
    }
    setBusy(false);
  };

  const generate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/invoices/generate", {
        cadence: gen.cadence,
        period_start: gen.period_start || null,
        period_end: gen.period_end || null,
      });
      toast.success(
        data.created
          ? `${data.created} draft invoice(s) built from completed work (${data.period_start} – ${data.period_end})`
          : "No uninvoiced completed work in that period"
      );
      setGenOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not generate invoices");
    }
    setBusy(false);
  };

  const setStatus = async (inv, status) => {
    try {
      await api.post(`/invoices/${inv._id}/status`, { status });
      toast.success(status === "sent" ? `Invoice emailed to ${inv.owner_name}` : `Invoice marked ${label(status)}`);
      load();
      setViewing(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update invoice");
    }
  };

  const remove = async (inv) => {
    try {
      await api.delete(`/invoices/${inv._id}`);
      toast.success("Draft invoice deleted");
      load();
      setViewing(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete invoice");
    }
  };

  const pay = async (inv, method = "credit") => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${inv._id}/checkout`,
        { origin_url: window.location.origin, method });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout");
      setBusy(false);
    }
  };

  const remind = async (inv) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${inv._id}/remind`, {});
      toast.success(`Payment reminder with a pay link emailed to ${data.to}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send the reminder");
    }
    setBusy(false);
  };

  const recordPayment = async (inv) => {    const amount = window.prompt(`Record a payment against ${inv.number} (balance ${money(inv.balance)})`,
      String(inv.balance));
    if (!amount) return;
    try {
      await api.post(`/invoices/${inv._id}/payments`, { amount: Number(amount), method: "manual" });
      toast.success("Payment recorded");
      load();
      setViewing(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not record payment");
    }
  };

  const openInvoice = async (inv) => {
    const { data } = await api.get(`/invoices/${inv._id}`);
    setViewing(data);
  };

  const visible = rows;

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Billing</p>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-royal-deep mt-2">Invoices</h1>
          <p className="text-muted-foreground mt-3 text-sm md:text-base">
            Weekly turnover cleans and monthly oversight, billed to your owners — drafted from completed work.
          </p>
        </div>
        {canCreate && (
          <div className="flex gap-2">
            <Button
              data-testid="generate-invoices-btn"
              variant="outline"
              onClick={() => setGenOpen(true)}
              className="h-11 gap-2 border-gold text-royal-deep hover:bg-gold/10"
            >
              <Wand2 className="h-4 w-4" /> Auto-draft
            </Button>
            <Button
              data-testid="new-invoice-btn"
              onClick={() => setOpen(true)}
              className="bg-royal hover:bg-royal-deep text-white gap-2 h-11"
            >
              <Plus className="h-4 w-4" /> New invoice
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="invoice-filters">
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`invoice-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`rounded-full border px-4 min-h-[40px] text-xs font-medium transition-colors duration-200 ${
              filter === f ? "bg-royal text-white border-royal" : "bg-white text-royal border-border hover:border-royal/40"
            }`}
          >
            {label(f)}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="rounded-lg border border-dashed border-royal/25 bg-white p-10 text-center">
          <Receipt className="h-7 w-7 text-gold mx-auto" />
          <p className="font-display text-lg font-semibold text-royal-deep mt-3">No invoices here yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            {canCreate
              ? "Use Auto-draft to turn completed work orders into owner invoices."
              : "Invoices from your management company will appear here."}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-white divide-y divide-border overflow-hidden"
             data-testid="invoices-list">
          {visible.map((inv) => (
            <button
              key={inv._id}
              data-testid={`invoice-row-${inv._id}`}
              onClick={() => openInvoice(inv)}
              className="w-full text-left px-4 sm:px-5 py-4 hover:bg-secondary/40 transition-colors duration-150"
            >
              <div className="flex flex-wrap items-center gap-3">
                <p className="font-display font-semibold text-royal-deep">{inv.number}</p>
                <StatusBadge value={inv.is_overdue && inv.status !== "paid" ? "overdue" : inv.status}
                             testId={`invoice-status-${inv._id}`} />
                <span className="rounded-full border border-royal/15 bg-royal/5 px-2 py-0.5 text-[11px] text-royal">
                  {label(inv.cadence)}
                </span>
                <p className="ml-auto font-display text-lg font-bold text-royal-deep">{money(inv.total)}</p>
              </div>
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>{inv.owner_name}{inv.is_guest ? " · Guest" : ""}</span>
                {inv.property_name && <span>{inv.property_name}</span>}
                {inv.period_start && <span>{inv.period_start} – {inv.period_end}</span>}
                <span>Due upon receipt</span>
                {inv.amount_paid > 0 && <span className="text-emerald-700">Paid {money(inv.amount_paid)}</span>}
              </div>
            </button>
          ))}
          <div ref={rowsSentinel} />
          {hasMoreRows && (
            <p data-testid="invoices-loading-more"
               className="flex items-center gap-2 px-4 py-3 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading more invoices…
            </p>
          )}
        </div>
      )}

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent data-testid="generate-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-royal-deep">Auto-draft invoices</DialogTitle>
            <DialogDescription>
              Pulls every completed, uninvoiced work order for each owner in the period and drafts their invoice.
              Weekly covers turnover cleans; monthly covers maintenance, oversight and issue reporting.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={generate} className="space-y-4" data-testid="generate-form">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Cadence</Label>
              <Select value={gen.cadence} onValueChange={(v) => setGen((g) => ({ ...g, cadence: v }))}>
                <SelectTrigger data-testid="generate-cadence-select" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly" data-testid="cadence-weekly">Weekly — turnover cleans</SelectItem>
                  <SelectItem value="monthly" data-testid="cadence-monthly">
                    Monthly — maintenance, oversight & issues
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">From</Label>
                <Input data-testid="generate-from-input" type="date" value={gen.period_start}
                  onChange={(e) => setGen((g) => ({ ...g, period_start: e.target.value }))} className="mt-1.5 h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">To</Label>
                <Input data-testid="generate-to-input" type="date" value={gen.period_end}
                  onChange={(e) => setGen((g) => ({ ...g, period_end: e.target.value }))} className="mt-1.5 h-11" />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Leave the dates empty to use last {gen.cadence === "weekly" ? "week" : "month"}.
            </p>
            <Button data-testid="run-generate-btn" type="submit" disabled={busy}
              className="w-full h-11 bg-royal hover:bg-royal-deep text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Draft invoices"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-royal-deep">New invoice</DialogTitle>
            <DialogDescription>
              Build a one-off charge and email it straight to the customer with a Pay Now link.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4" data-testid="invoice-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Bill to</Label>
                <Select value={form.owner_id || "guest"}
                  onValueChange={(v) => setForm((f) => ({ ...f, owner_id: v === "guest" ? "" : v }))}>
                  <SelectTrigger data-testid="invoice-owner-select" className="mt-1.5 h-11">
                    <SelectValue placeholder="Select owner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="guest" data-testid="invoice-owner-option-guest">
                      Guest customer (no account)
                    </SelectItem>
                    {owners.map((o) => (
                      <SelectItem key={o._id} value={o._id} data-testid={`invoice-owner-option-${o._id}`}>
                        {o.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Property (optional)</Label>
                <Select value={form.property_id || "none"}
                  onValueChange={(v) => setForm((f) => ({ ...f, property_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="invoice-property-select" className="mt-1.5 h-11">
                    <SelectValue placeholder="No specific property" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific property</SelectItem>
                    {properties.map((p) => (
                      <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!form.owner_id && (
              <div className="grid gap-4 sm:grid-cols-2 rounded-lg border border-dashed border-gold/50 bg-gold/5 p-4"
                   data-testid="guest-fields">
                <div className="sm:col-span-2">
                  <p className="text-xs text-royal-deep">
                    Guest invoice — they pay by card from the emailed link, no account or sign-in needed.
                  </p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Guest name</Label>
                  <Input data-testid="guest-name-input" value={form.guest_name}
                    onChange={(e) => setForm((f) => ({ ...f, guest_name: e.target.value }))}
                    placeholder="Full name" className="mt-1.5 h-11" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Guest email</Label>
                  <Input data-testid="guest-email-input" type="email" value={form.guest_email}
                    onChange={(e) => setForm((f) => ({ ...f, guest_email: e.target.value }))}
                    placeholder="name@example.com" className="mt-1.5 h-11" />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Line items</Label>
              {form.line_items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2" data-testid={`line-item-${idx}`}>
                  <Input data-testid={`line-desc-${idx}`} className="col-span-6 h-11" placeholder="Description"
                    value={item.description} onChange={(e) => setItem(idx, { description: e.target.value })} />
                  <Input data-testid={`line-qty-${idx}`} className="col-span-2 h-11" type="number" min="0" step="0.5"
                    value={item.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} />
                  <Input data-testid={`line-price-${idx}`} className="col-span-3 h-11" type="number" min="0"
                    step="0.01" value={item.unit_price}
                    onChange={(e) => setItem(idx, { unit_price: e.target.value })} />
                  <button type="button" data-testid={`remove-line-${idx}`}
                    onClick={() => setForm((f) => ({ ...f,
                      line_items: f.line_items.filter((_, x) => x !== idx) }))}
                    className="col-span-1 text-muted-foreground hover:text-destructive transition-colors duration-150">
                    <Trash2 className="h-4 w-4 mx-auto" />
                  </button>
                </div>
              ))}
              <Button type="button" data-testid="add-line-btn" variant="outline"
                onClick={() => setForm((f) => ({ ...f,
                  line_items: [...f.line_items, { description: "", quantity: 1, unit_price: 0 }] }))}
                className="h-10 text-xs border-royal/30 text-royal hover:bg-royal/5 gap-1.5">
                <Plus className="h-3.5 w-3.5" /> Add line
              </Button>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tax %</Label>
                <Input data-testid="invoice-tax-input" type="number" min="0" step="0.01" value={form.tax_rate}
                  onChange={(e) => setForm((f) => ({ ...f, tax_rate: e.target.value }))} className="mt-1.5 h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Discount</Label>
                <Input data-testid="invoice-discount-input" type="number" min="0" step="0.01" value={form.discount}
                  onChange={(e) => setForm((f) => ({ ...f, discount: e.target.value }))} className="mt-1.5 h-11" />
              </div>
              <div className="flex items-end">
                <p className="text-sm text-muted-foreground">
                  Subtotal <span className="font-display text-lg font-bold text-royal-deep">{money(totals)}</span>
                </p>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
              <Textarea data-testid="invoice-notes-input" rows={2} value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="mt-1.5" />
            </div>

            <Button data-testid="save-invoice-btn" type="submit"
              disabled={busy || (!form.owner_id && !(form.guest_name.trim() && form.guest_email.includes("@")))}
              className="w-full h-12 bg-royal hover:bg-royal-deep text-white font-semibold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send invoice"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="invoice-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-royal-deep flex items-center gap-3">
              {viewing?.number}
              {viewing && <StatusBadge value={viewing.status} testId="detail-invoice-status" />}
            </DialogTitle>
            <DialogDescription>
              {viewing?.owner_name}
              {viewing?.property_name ? ` · ${viewing.property_name}` : ""} · due upon receipt
            </DialogDescription>
          </DialogHeader>
          {viewing && (
            <div className="space-y-4">
              <div className="rounded-md border border-border divide-y divide-border">
                {viewing.line_items.map((i, idx) => (
                  <div key={idx} className="flex items-start gap-3 px-3 py-2.5" data-testid={`detail-line-${idx}`}>
                    <p className="text-sm text-royal-deep flex-1">{i.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {i.quantity} × {money(i.unit_price)}
                    </p>
                    <p className="text-sm font-semibold text-royal-deep w-20 text-right">{money(i.amount)}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-1 text-sm">
                {[
                  ["Subtotal", viewing.subtotal],
                  ["Discount", -viewing.discount],
                  ["Tax", viewing.tax_amount],
                ].map(([k, v]) => (
                  <p key={k} className="flex justify-between text-muted-foreground">
                    <span>{k}</span>
                    <span>{money(v)}</span>
                  </p>
                ))}
                <p className="flex justify-between font-display text-lg font-bold text-royal-deep pt-1 border-t border-border">
                  <span>Total</span>
                  <span data-testid="detail-invoice-total">{money(viewing.total)}</span>
                </p>
                <p className="flex justify-between text-emerald-700">
                  <span>Paid</span>
                  <span>{money(viewing.amount_paid)}</span>
                </p>
                <p className="flex justify-between font-semibold text-royal">
                  <span>Balance due</span>
                  <span data-testid="detail-invoice-balance">{money(viewing.balance)}</span>
                </p>
              </div>

              {(viewing.payments || []).length > 0 && (
                <div className="rounded-md bg-secondary/60 p-3" data-testid="detail-payments">
                  <p className="text-xs font-semibold text-royal-deep uppercase tracking-wider">Payments</p>
                  {viewing.payments.map((p, idx) => (
                    <p key={idx} className="text-xs text-muted-foreground mt-1">
                      {money(p.amount)} · {p.method} · {new Date(p.at).toLocaleDateString()}
                      {p.reference ? ` · ${p.reference}` : ""}
                    </p>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {canUpdate && viewing.status === "draft" && (
                  <Button data-testid="send-invoice-btn" onClick={() => setStatus(viewing, "sent")}
                    className="bg-royal hover:bg-royal-deep text-white gap-2 h-11">
                    <Send className="h-4 w-4" /> Send to owner
                  </Button>
                )}
                {canPay && viewing.balance > 0 && !["draft", "void"].includes(viewing.status) && (
                  <div className="w-full">
                    <p className="text-xs text-muted-foreground mb-2" data-testid="in-app-fee-note">
                      The payer chooses at checkout: credit card adds the 2% surcharge, debit card and
                      bank transfer carry no surcharge.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button data-testid="pay-invoice-btn" disabled={busy}
                        onClick={() => pay(viewing, "credit")}
                        className="bg-gold hover:bg-gold/90 text-royal-deep font-semibold gap-2 h-11">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                        Pay With Credit Card
                      </Button>
                      <Button data-testid="pay-invoice-debit-btn" disabled={busy} variant="outline"
                        onClick={() => pay(viewing, "debit")}
                        className="border-royal/40 text-royal hover:bg-royal/5 font-semibold gap-2 h-11">
                        <CreditCard className="h-4 w-4" /> Pay With Debit Card
                      </Button>
                    </div>
                  </div>
                )}
                {canUpdate && viewing.balance > 0 && !["draft", "void"].includes(viewing.status) && (
                  <Button data-testid="remind-invoice-btn" variant="outline" disabled={busy}
                    onClick={() => remind(viewing)}
                    className="h-11 border-gold text-royal-deep hover:bg-gold/10 gap-2">
                    <Bell className="h-4 w-4" /> Send payment reminder
                  </Button>
                )}
                {canUpdate && viewing.balance > 0 && viewing.status !== "draft" && (
                  <Button data-testid="record-payment-btn" variant="outline" onClick={() => recordPayment(viewing)}
                    className="h-11 border-royal/30 text-royal hover:bg-royal/5">
                    Record a payment
                  </Button>
                )}
                {canUpdate && !["paid", "void"].includes(viewing.status) && (
                  <Button data-testid="void-invoice-btn" variant="outline" onClick={() => setStatus(viewing, "void")}
                    className="h-11 border-destructive/40 text-destructive hover:bg-destructive/5 gap-2">
                    <Ban className="h-4 w-4" /> Void
                  </Button>
                )}
                {canCreate && viewing.status === "draft" && (
                  <Button data-testid="delete-invoice-btn" variant="outline" onClick={() => remove(viewing)}
                    className="h-11 border-destructive/40 text-destructive hover:bg-destructive/5 gap-2">
                    <Trash2 className="h-4 w-4" /> Delete
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
